/**
 * In-memory cache with TTL and stale-while-revalidate support.
 * --------------------------------------------------------------------------
 * Simple LRU cache for browser environments. No persistence (use IndexedDB for that).
 */

const cache = new Map();
const MAX_SIZE = 200;

function cleanup() {
  const now = Date.now();
  for (const [key, entry] of cache.entries()) {
    if (entry.expiresAt && now > entry.expiresAt) {
      cache.delete(key);
    }
  }
  if (cache.size > MAX_SIZE) {
    const entries = [...cache.entries()];
    entries.sort((a, b) => a[1].createdAt - b[1].createdAt);
    const toDelete = entries.slice(0, cache.size - MAX_SIZE);
    toDelete.forEach(([key]) => cache.delete(key));
  }
}

function createEntry(data, ttl) {
  const now = Date.now();
  return {
    data,
    createdAt: now,
    expiresAt: ttl ? now + ttl : null,
    staleAt: ttl ? now + ttl * 0.5 : null, // stale at 50% TTL
  };
}

export function createCacheKey(...parts) {
  return parts.filter(Boolean).join(':');
}

export function getCache(key) {
  cleanup();
  const entry = cache.get(key);
  if (!entry) return { data: null, isStale: false, age: 0 };

  const now = Date.now();
  const age = now - entry.createdAt;
  const isExpired = entry.expiresAt && now > entry.expiresAt;
  const isStale = entry.staleAt && now > entry.staleAt;

  if (isExpired) {
    cache.delete(key);
    return { data: null, isStale: false, age };
  }

  return {
    data: entry.data,
    isStale,
    age,
    fromCache: true,
  };
}

export function setCache(key, data, ttl) {
  cleanup();
  cache.set(key, createEntry(data, ttl));
}

export function deleteCache(key) {
  cache.delete(key);
}

export function clearCache() {
  cache.clear();
}

export function getCacheStats() {
  cleanup();
  return {
    size: cache.size,
    maxSize: MAX_SIZE,
    keys: [...cache.keys()],
  };
}

/**
 * Fetch with cache - supports stale-while-revalidate pattern
 *
 * @param {string} key - cache key
 * @param {Function} fetcher - async function returning data
 * @param {object} options
 * @param {number} options.ttl - TTL in ms
 * @param {boolean} options.forceRefresh - bypass cache
 * @returns {Promise<{data, fromCache, isStale, age}>}
 */
export async function fetchWithCache(key, fetcher, { ttl = 60000, forceRefresh = false } = {}) {
  if (!forceRefresh) {
    const cached = getCache(key);
    if (cached.data !== null) {
      if (!cached.isStale) {
        return { data: cached.data, fromCache: true, isStale: false, age: cached.age };
      }
      // Stale - return cached data but trigger background refresh
      const backgroundPromise = fetcher()
        .then((fresh) => {
          setCache(key, fresh, ttl);
          return fresh;
        })
        .catch(() => cached.data); // on error, keep stale

      return { data: cached.data, fromCache: true, isStale: true, age: cached.age, revalidating: backgroundPromise };
    }
  }

  // No cache or forced refresh
  const fresh = await fetcher();
  setCache(key, fresh, ttl);
  return { data: fresh, fromCache: false, isStale: false, age: 0 };
}